import { PaymentError } from './payment-error';

export interface RetryConfig {
    maxRetries: number;
    baseDelay: number;
    maxDelay: number;
    jitterFactor: number;
}

export interface PollingConfig {
    baseInterval: number;
    jitterFactor: number;
    maxInterval?: number;
    backoffMultiplier?: number;
    maxAttempts?: number;
    timeoutMs?: number;
}

export interface PollingTimeoutContext {
    reason: 'elapsed_time' | 'max_attempts';
    attempt: number;
    timeoutMs?: number;
    maxAttempts?: number;
    elapsedMs?: number;
}

type PollingTimeoutReporter = (
    error: PaymentError,
    context: PollingTimeoutContext
) => void;

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    jitterFactor: 0.1
};

export const DEFAULT_POLLING_CONFIG: PollingConfig = {
    baseInterval: 50,
    jitterFactor: 0.1,
    maxInterval: 5000,
    backoffMultiplier: 1.5,
    maxAttempts: 60,
    timeoutMs: 30000
};

/**
 * Executes an asynchronous operation with retry capability using exponential backoff and jitter.
 *
 * @param operation - The asynchronous operation to execute.
 * @param config - Configuration for retry behavior.
 * @param onError - Optional callback to handle errors.
 * @returns The result of the successful operation.
 * @throws PaymentError if all retry attempts fail.
 */
export async function executeWithRetry<T>(
    operation: () => Promise<T>,
    config: RetryConfig = DEFAULT_RETRY_CONFIG,
    onError?: (error: PaymentError) => void
): Promise<T> {
    let attempts = 0;
    let lastError: PaymentError | null = null;

    while (attempts < config.maxRetries) {
        try {
            return await operation();
        } catch (error) {
            attempts++;
            lastError = PaymentError.fromUnknown(error);

            if (!shouldRetryError(lastError) || attempts >= config.maxRetries) {
                if (onError) {
                    onError(lastError);
                }
                throw lastError;
            }

            const delay = calculateBackoffDelay(attempts, config);
            await delayPromise(delay);
        }
    }

    throw (
        lastError || new PaymentError('Retry operation failed', 'GENERIC_ERROR')
    );
}

/**
 * Schedules the next poll operation with adaptive backoff and jitter.
 *
 * @param operation - The asynchronous operation to execute.
 * @param config - Polling configuration parameters.
 * @param attempt - Current polling attempt number (starts at 0).
 * @returns A timeout handle that can be used to cancel the scheduled operation.
 */
export function scheduleNextPoll(
    operation: () => Promise<void>,
    config: PollingConfig = DEFAULT_POLLING_CONFIG,
    attempt = 0
): ReturnType<typeof setTimeout> {
    const delay = calculateAdaptivePollingDelay(config, attempt);
    return setTimeout(operation, delay);
}

function shouldRetryError(error: PaymentError): boolean {
    if (error.code === 'VALIDATION_ERROR') {
        return false;
    }
    const isServerError =
        error.httpStatus !== undefined && error.httpStatus >= 500;
    return error.code === 'NETWORK_ERROR' || isServerError;
}

function calculateBackoffDelay(attempt: number, config: RetryConfig): number {
    const exponentialDelay = Math.min(
        config.baseDelay * 2 ** (attempt - 1),
        config.maxDelay
    );
    return addJitter(exponentialDelay, config.jitterFactor);
}

function calculateAdaptivePollingDelay(
    config: PollingConfig,
    attempt: number
): number {
    const backoffMultiplier = config.backoffMultiplier || 1.5;
    const maxInterval = config.maxInterval || 5000;

    const exponentialDelay = Math.min(
        config.baseInterval * backoffMultiplier ** Math.floor(attempt / 3),
        maxInterval
    );

    return addJitter(exponentialDelay, config.jitterFactor);
}

function addJitter(delay: number, factor: number): number {
    const jitter = delay * factor;
    return delay + (Math.random() * 2 - 1) * jitter;
}

function delayPromise(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export class PollingAbortedError extends Error {
    constructor() {
        super('Polling was stopped');
        this.name = 'PollingAbortedError';
    }
}

export const isPollingAbortedError = (
    error: unknown
): error is PollingAbortedError => error instanceof PollingAbortedError;

export class PollingManager {
    private attempt = 0;
    private timeout?: ReturnType<typeof setTimeout>;
    private config: PollingConfig;
    private startTime: number | null = null;
    private timeoutReported = false;
    private reportTimeout: PollingTimeoutReporter;
    private abortController = new AbortController();

    constructor(
        reportTimeout: PollingTimeoutReporter,
        config: PollingConfig = DEFAULT_POLLING_CONFIG
    ) {
        this.reportTimeout = reportTimeout;
        this.config = config;
    }

    public getSignal = (): AbortSignal => this.abortController.signal;

    public schedule(
        operation: () => Promise<void>,
        onError?: (error: unknown) => void,
        signal: AbortSignal = this.getSignal()
    ): void {
        if (signal.aborted) {
            return;
        }

        this.clear();

        if (this.startTime === null) {
            this.startTime = performance.now();
        }

        if (this.isTimedOut()) {
            this.handleTimeout('elapsed_time', onError);
            return;
        }

        if (this.isMaxAttemptsReached()) {
            this.handleTimeout('max_attempts', onError);
            return;
        }

        const delay = calculateAdaptivePollingDelay(this.config, this.attempt);
        this.timeout = setTimeout(async () => {
            try {
                await operation();
                this.attempt++;
            } catch (error) {
                this.attempt++;
                onError?.(error);
            }
        }, delay);
    }

    public clear(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = undefined;
        }
    }

    public reset(): void {
        this.clear();
        this.abortController.abort();
        this.abortController = new AbortController();
        this.attempt = 0;
        this.startTime = null;
        this.timeoutReported = false;
    }

    public getAttempt(): number {
        return this.attempt;
    }

    private isTimedOut(): boolean {
        return (
            this.config.timeoutMs !== undefined &&
            this.startTime !== null &&
            performance.now() - this.startTime >= this.config.timeoutMs
        );
    }

    private isMaxAttemptsReached(): boolean {
        return (
            this.config.maxAttempts !== undefined &&
            this.attempt >= this.config.maxAttempts
        );
    }

    private createTimeoutError(): PaymentError {
        return new PaymentError(
            'The payment is taking longer than expected. Please try again.',
            'POLLING_TIMEOUT'
        );
    }

    private handleTimeout = (
        reason: 'elapsed_time' | 'max_attempts',
        onError?: (error: unknown) => void
    ): void => {
        const error = this.createTimeoutError();
        if (!this.timeoutReported) {
            this.timeoutReported = true;
            this.reportTimeout(error, {
                reason,
                attempt: this.attempt,
                timeoutMs: this.config.timeoutMs,
                maxAttempts: this.config.maxAttempts,
                elapsedMs:
                    this.startTime === null
                        ? undefined
                        : performance.now() - this.startTime
            });
        }
        onError?.(error);
    };
}
